import { GWUtils } from '~/utils/ca-utils';

/**
 * Service class for interacting with the Boarderie API
 */
class CAOrderApiService {
  constructor() {
    this.baseUrl = 'https://boarderie-emails-d1852672b614.herokuapp.com';

    this.routes = {
      egift: {
        get: '/customer-egift',
        edit: '/customer-egift-edit',
        resend: '/customer-egift-resend',
      },
      physical: {
        edit: '/customer-phisycal-edit',
      },
    };
  }

  /**
   * Get the full URL for a given endpoint
   * @param {string} endpoint - The API endpoint
   * @returns {string} The full URL
   */
  getUrl(endpoint) {
    return `${this.baseUrl}${endpoint}`;
  }

  /**
   * Create the standard auth payload with customer data
   * @param {Object} additionalData - Additional data to include in the payload
   * @returns {Object} The complete payload with auth data
   */
  createAuthPayload(additionalData = {}) {
    return {
      customerID: GWUtils.customerId,
      email: GWUtils.customerEmail,
      token: GWUtils.customerToken,
      ...additionalData,
    };
  }

  /**
   * Make a POST request to the API
   * @param {string} endpoint - The API endpoint
   * @param {Object} data - The data to send in the request
   * @returns {Promise<Object>} The API response
   */
  async post(endpoint, data) {
    try {
      const response = await fetch(this.getUrl(endpoint), {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      return await response.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  /**
   * Fetch e-gift data for a specific line item
   * @param {string} lineItemId - The line item ID
   * @returns {Promise<Object>} The e-gift data
   */
  async fetchEgiftData(lineItemId) {
    try {
      const payload = this.createAuthPayload({ lineID: lineItemId });
      const data = await this.post(this.routes.egift.get, payload);

      return data?.message || null;
    } catch (error) {
      console.error('Error fetching egift data:', error);
      throw error;
    }
  }

  /**
   * Edit an e-gift
   * @param {string} lineItemId - The line item ID
   * @param {Object} editData - The data to update (sentBy, message, recEmail, etc.)
   * @returns {Promise<Object>} The API response
   */
  async editEgift(lineItemId, editData) {
    try {
      const payload = this.createAuthPayload({
        lineID: lineItemId,
        ...editData,
      });

      return await this.post(this.routes.egift.edit, payload);
    } catch (error) {
      console.error('Error editing e-gift:', error);
      throw error;
    }
  }

  /**
   * Resend an e-gift
   * @param {string} lineItemId - The line item ID
   * @returns {Promise<Object>} The API response
   */
  async resendEgift(lineItemId) {
    try {
      const payload = this.createAuthPayload({ lineID: lineItemId });
      return await this.post(this.routes.egift.resend, payload);
    } catch (error) {
      console.error('Error resending e-gift:', error);
      throw error;
    }
  }

  /**
   * Edit a physical order
   * @param {string} orderId - The order ID
   * @param {string} lineItemId - The line item ID
   * @param {Object} editData - The order data to update (includes message, from, address, etc.)
   * @returns {Promise<Object>} The API response
   */
  async editPhysicalOrder(orderId, lineItemId, editData) {
    try {
      const payload = this.createAuthPayload({
        lineID: lineItemId,
        orderID: orderId,
        ...editData,
      });

      return await this.post(this.routes.physical.edit, payload);
    } catch (error) {
      console.error('Error editing physical order:', error);
      throw error;
    }
  }
}

const caOrderApi = new CAOrderApiService();

export default caOrderApi;
